import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  ButtonGroup,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ConfirmationDialog from "../components/ConfirmationDialog";
import LazyLoader from "../components/LazyLoader";
import SearchField from "../components/SearchField";
import useSnackbar from "../components/snackbar/useSnackBar";
import SortingBar from "../components/SortingBar";
import StyledTab from "../components/TabPanel/StyledTab";
import StyledTabs from "../components/TabPanel/StyledTabs";
import { useApi } from "../hooks/useApi";
import { useLanguage } from "../hooks/useLanguage";

export default function Material({ userInfo }) {
  const [materials, setMaterials] = useState([]);
  const [page, setPage] = useState(1);
  const [endReached, setEndReached] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateCounter, setUpdateCounter] = useState(1);
  const [query, setQuery] = useState("");
  // if the dialog for deletion is open
  const [isOpen, setIsOpen] = useState({ state: false, id: null });
  const [isOpenForce, setIsOpenForce] = useState({ state: false, id: null });
  // sorting-states
  const [sortType, setSortType] = useState('MODIFICATION_DATE');
  const [sortDir, setSortDir] = useState('DESC');

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = searchParams.get('tab') ?? 'private';
  const accessFlags = useMemo(() => {
    switch (selectedTab) {
      case 'private':
        return { public: true, all: true };
        break;
      case 'public':
        return { public: true, all: false };
        break;
      case 'additional':
        return { public: false, all: true };
        break;
    }
  }, [selectedTab]);

  const bulkFileRef = useRef();

  const { locale } = useLanguage();

  const intl = useIntl();
  const api = useApi();

  const { openSnackBar, closeSnackBar } = useSnackbar();

  const navigate = useNavigate();

  useEffect(() => {
    if (page === 1 || endReached) return;
    fetchPage(page, query, accessFlags, sortType, sortDir);
  }, [page]);

  useEffect(() => {
    setPage(1);
    fetchPage(1, query, accessFlags, sortType, sortDir);
  }, [query, accessFlags, sortType, sortDir, updateCounter, locale]);

  /**
   * Fetches the specified page from the server.
   * @param {*} page 
   * @param {*} query 
   * @param {*} accessFlags
   * @param {*} sortType 
   * @param {*} sortDir 
   */
  const fetchPage = (page, query, accessFlags, sortType, sortDir) => {
    let fetchURL = '/material';
    fetchURL += `?page=${page - 1}&size=10`;
    if (query) {
      fetchURL += `&search=${query}`;
    }
    fetchURL += `&published=${accessFlags.public}&all=${accessFlags.all}&sortType=${sortType}&sortDir=${sortDir}&lang=${locale}`;
    setIsLoading(true);
    api
      .get(fetchURL)
      .then(response => {
        if (page === 1) {
          setMaterials(response.data.content);
        } else {
          setMaterials(old => [...old, ...response.data.content]);
        }
        setEndReached(response.data.last);
      })
      .catch(error => {
        console.log(error);
        if (error?.response?.status === 403) {
          navigate('/login');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleOpen = (id) => {
    setIsOpen({ state: true, id: id });
  };

  const handleClose = () => {
    setIsOpen({ state: false, id: null });
  };

  const handleOpenForceDialog = (id) => {
    setIsOpenForce({ state: true, id: id });
  };

  const handleCloseForceDialog = () => {
    setIsOpenForce({ state: false });
  };

  const handleSearch = (value) => {
    setQuery(value);
  };

  /**
  * Should be called when the sorting-type is changed.
  * @param {*} value 
  */
  const handleChangeSortType = (value) => {
    setSortType(value);
  }

  /**
   * Should be called when the sorting-direction is changed.
   * @param {*} value 
   */
  const handleChangeSortDir = (value) => {
    setSortDir(value);
  }

  /**
   * Handles the change of the selected tab.
   * @param {*} selectedTab 
   * @returns 
   */
  const handleChangeTab = (_, newValue) => {
    setSearchParams({ tab: newValue });
  };

  const dialog = useMemo(() => {
    let d = (
      <ConfirmationDialog
        title={intl.formatMessage({ id: "Material.deletionQuestion" })}
        content={intl.formatMessage({ id: "Overview.deletionInfo" })}
        isOpen={isOpen.state}
        handleAccept={() => handleDelete(isOpen.id)}
        handleClose={handleClose}
      />
    );
    if (isOpen.state) {
      return d;
    }
  }, [isOpen]);

  const dialogForceDelete = useMemo(() => {
    let d = (
      <ConfirmationDialog
        title={intl.formatMessage({ id: "Overview.deletionForceQuestion" })}
        content={intl.formatMessage({ id: "Overview.deletionInfo" })}
        isOpen={isOpenForce.state}
        handleAccept={() => handleDelete(isOpenForce.id, true)}
        handleClose={handleCloseForceDialog}
      />
    );
    if (isOpenForce.state) {
      return d;
    }
  }, [isOpenForce]);

  function handleDelete(id, force = false) {
    api
      .delete(`/material/${id}?force=${force}`)
      .then((response) => {
        // handle the response
        if (force) {
          handleCloseForceDialog();
        } else {
          handleClose();
        }
        setUpdateCounter(prev => prev + 1);
      })
      .catch((error) => {
        // handle errors
        if (!force && error.response && error.response.status === 409) {
          // the process must be repeated with the force-flag
          handleClose();
          handleOpenForceDialog(id);
        } else {
          console.log(error);
          if (force) {
            handleCloseForceDialog();
          } else {
            handleClose();
          }
          openSnackBar(intl.formatMessage({ id: "Overview.deletionFailure" }));
        }
      });
  }

  const materialList = useMemo(() => {
    return materials.map((row) => (
      <TableRow
        key={row.id}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell component="th" scope="row">
          {row.name}
        </TableCell>
        <TableCell align="right">{row.width}</TableCell>
        <TableCell align="right">{row.length}</TableCell>
        <TableCell align="right">
          <ButtonGroup
            variant="contained"
            aria-label="outlined primary button group"
          >
            <Button
              color="primary"
              aria-label="edit"
              onClick={() => navigate(`/material/${row.id}`, { state: { fromTab: selectedTab } })}
            >
              <EditIcon />
            </Button>
            {(userInfo?.isAdmin || row.ownerId === userInfo?.ownerId) && <Button
              component={Link}
              onClick={() => handleOpen(row.id)}
              color="error"
              aria-label="delete"
            >
              <DeleteIcon />
            </Button>}
          </ButtonGroup>
        </TableCell>
      </TableRow>
    ));
  }, [materials]);

  /**
   * Uploads the selected zip-file to the server and increments the update-counter in case of success.
   * @param {*} files 
   * @returns 
   */
  const handleBulkFileSelect = (files) => {
    const file = files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    api.post("/material/bulk", formData)
      .then(() => {
        setUpdateCounter(prev => prev + 1);
      })
      .catch(error => {
        console.log(error);
      });
  };

  return (
    <Container>
      <input accept=".zip" ref={bulkFileRef} hidden type="file" onChange={event => handleBulkFileSelect(event?.target?.files)} onClick={event => event.target.value = ''} />
      <Button
        variant="contained"
        color="success"
        component={Link}
        to="/material/new"
        sx={{ float: "right" }}
      >
        <FormattedMessage id="Overview.create" />
      </Button>
      <Button
        variant="contained"
        color="yellow"
        onClick={() => bulkFileRef.current?.click()}
        sx={{ float: "right", marginRight: 2 }}
      >
        <FormattedMessage id="Overview.bulkUpload" />
      </Button>
      <h3><FormattedMessage id="Material.heading" /></h3>
      <Box sx={{ width: '100%', marginBottom: 1 }}>
        <StyledTabs
          value={selectedTab}
          onChange={handleChangeTab}
          aria-label="style data tabs"
        >
          <StyledTab value='private' label={intl.formatMessage({ id: "Material.filterPrivate" })} />
          <StyledTab value='public' label={intl.formatMessage({ id: "Material.filterPublic" })} />
          {userInfo?.isAdmin && <StyledTab value='additional' label={intl.formatMessage({ id: "Material.filterPrivateOthers" })} />}
        </StyledTabs>
      </Box>
      <SearchField onChange={(value) => handleSearch(value)} />
      <SortingBar onChangeSortType={handleChangeSortType} onChangeSortDir={handleChangeSortDir}
        defaultSortType="MODIFICATION_DATE" defaultSortDir="DESC" />
      <Box sx={{ width: "100%", height: "100%", margin: 0, padding: 0, marginTop: 1 }}>
        <LazyLoader
          onLoad={() => setPage(prev => prev + 1)}
          loading={isLoading}
          endReached={endReached}
        >
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell><FormattedMessage id="Material.name" /></TableCell>
                  <TableCell align="right"><FormattedMessage id="Material.width" /></TableCell>
                  <TableCell align="right"><FormattedMessage id="Material.length" /></TableCell>
                  <TableCell align="right"><FormattedMessage id="Overview.actions" /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {materialList}
              </TableBody>
            </Table>
          </TableContainer>
        </LazyLoader>
      </Box>
      {dialog}
      {dialogForceDelete}
    </Container>
  );
}
